import { Controller, useFormContext } from "react-hook-form";
import type { SxProps, Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { TextField } from "ui/text-field";
import { Typography } from "ui/typography";

import { Button } from "ui/button";
import { DialogActions, DialogBackButton } from "ui/dialog";
import { useCampaignWithWriterContext } from "components/modals/create-campaign-with-writer-modal/context/campaign-with-writer-context";
import {
  formatAudienceSegmentsLabel,
  getCampaignObjectiveLabel,
  getPreviousStep,
  shouldSkipCreativesStep,
  type CampaignTemplateSummary,
} from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.helper";
import { useSubmitCampaignWithWriter } from "components/modals/create-campaign-with-writer-modal/hooks/use-submit-campaign-with-writer";
import type { CreateCampaignWithWriterFormData } from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.types";

const ROOT_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const INTRO_SX: SxProps<Theme> = (theme) => ({ color: theme.palette.text.secondary });

const FIELDS_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const BOLD_LABEL_SX: SxProps<Theme> = { fontWeight: 700 };

const DETAILS_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

const DETAIL_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
};

const DETAIL_LABEL_SX: SxProps<Theme> = (theme) => ({
  fontWeight: 700,
  color: theme.palette.grey[900],
});

const DETAIL_VALUE_SX: SxProps<Theme> = (theme) => ({ color: theme.palette.grey[900] });

const VALIDATION_ERROR_SX: SxProps<Theme> = (theme) => ({ color: theme.palette.error.main });

const ReadOnlyDetail = ({ label, value }: { label: string; value: string }) => (
  <Box sx={DETAIL_SX}>
    <Typography component="span" variant="inherit" sx={DETAIL_LABEL_SX}>
      {label}
    </Typography>
    <Typography component="span" variant="inherit" sx={DETAIL_VALUE_SX}>
      {value || "—"}
    </Typography>
  </Box>
);

type CampaignDetailsStepProps = {
  templateSummary: CampaignTemplateSummary;
  templateValidationError: string | null;
};

export const CampaignDetailsStep = ({
  templateSummary,
  templateValidationError,
}: CampaignDetailsStepProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<CreateCampaignWithWriterFormData>();

  const campaignObjectiveLabel = getCampaignObjectiveLabel(templateSummary.campaignObjective);
  const audienceSegmentLabel = formatAudienceSegmentsLabel(templateSummary.audienceSegments);

  return (
    <Box sx={ROOT_SX}>
      <Typography component="p" variant="inherit" sx={INTRO_SX}>
        Please edit campaign details as needed and proceed.
      </Typography>

      <Box sx={FIELDS_SX}>
        <Controller
          name="campaignName"
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <TextField
              label={
                <Typography component="span" variant="inherit" sx={BOLD_LABEL_SX}>
                  Campaign name
                </Typography>
              }
              value={value || ""}
              onChange={(event) => onChange(event.target.value)}
              required
              errMessage={errors.campaignName?.message as string}
              {...field}
            />
          )}
        />

        <Controller
          name="cdnSubdirectoryName"
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <TextField
              label={
                <Typography component="span" variant="inherit" sx={BOLD_LABEL_SX}>
                  Campaign CDN Profile
                </Typography>
              }
              value={value || ""}
              onChange={(event) => onChange(event.target.value)}
              helperText="Enter a profile name with no spaces. This name will be used to organize this campaign's digital assets and landing pages at its destination URL."
              errMessage={errors.cdnSubdirectoryName?.message as string}
              {...field}
            />
          )}
        />

        <Box sx={DETAILS_SX}>
          <ReadOnlyDetail label="Campaign objective" value={campaignObjectiveLabel} />
          <ReadOnlyDetail label="Audience segment" value={audienceSegmentLabel} />
        </Box>

        {templateValidationError && (
          <Typography component="p" variant="body2" sx={VALIDATION_ERROR_SX}>
            {templateValidationError}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

type CampaignDetailsStepActionsProps = {
  templateValidationError: string | null;
  hasCreatives: boolean;
};

export const CampaignDetailsStepActions = ({
  templateValidationError,
  hasCreatives,
}: CampaignDetailsStepActionsProps) => {
  const {
    watch,
    formState: { isValid },
  } = useFormContext<CreateCampaignWithWriterFormData>();
  const includeCreatives = watch("includeCreatives");
  const skipCreativesStep = shouldSkipCreativesStep(hasCreatives, includeCreatives);

  const { step, next: handleNext, goTo } = useCampaignWithWriterContext();
  const handleBack = () => goTo(getPreviousStep(step, skipCreativesStep));
  const { handleCreateCampaign, isContinueDisabled: isCreateCampaignDisabled } =
    useSubmitCampaignWithWriter(templateValidationError);

  if (skipCreativesStep) {
    return (
      <DialogActions
        type="steps"
        back={
            <DialogBackButton onClick={handleBack}>Back to details</DialogBackButton>
          }
        primary={
            <Button size="large" disabled={isCreateCampaignDisabled} onClick={handleCreateCampaign}>
              Create campaign
            </Button>
          }
      />
    );
  }

  return (
    <DialogActions
      type="steps"
      back={
          <DialogBackButton onClick={handleBack}>Back to details</DialogBackButton>
        }
      primary={
          <Button size="large" disabled={!isValid || Boolean(templateValidationError)} onClick={handleNext}>
            Continue to prompt
          </Button>
        }
    />
  );
};
